import { useEffect, useId, useRef, useState } from 'react';

type AccountTypeOption = {
  key: string;
  label: string;
};

type AccountTypeSelectProps = {
  value: string;
  options: AccountTypeOption[];
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

/**
 * Custom select — menu is always clamped to the trigger width (fixes mobile overflow).
 */
export function AccountTypeSelect({
  value,
  options,
  loading = false,
  disabled = false,
  hasError = false,
  onChange,
  onBlur,
}: AccountTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.key === value);
  const label = loading
    ? 'Loading…'
    : selected?.label || 'Select account type';
  const isPlaceholder = !selected && !loading;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onBlur]);

  return (
    <div ref={rootRef} className="signup-type-select relative w-full min-w-0">
      <button
        type="button"
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className={`signup-input box-border flex h-[52px] w-full min-w-0 items-center justify-between gap-2 rounded-[16px] border bg-white px-3.5 text-left text-sm leading-none outline-none transition-[border-color,box-shadow] disabled:cursor-not-allowed disabled:opacity-60 ${
          open ? 'signup-type-open' : ''
        } ${
          hasError
            ? 'border-red-400'
            : 'border-[#EAE8F5] hover:border-[#C9B7E8]'
        } ${isPlaceholder ? 'text-[#8B9895]' : 'text-[#3A3660]'}`}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          className={`shrink-0 text-[#7C8B88] transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        >
          <path
            d="M1 1L6 6L11 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="signup-type-menu absolute top-[calc(100%+6px)] right-0 left-0 z-50 m-0 max-h-[220px] list-none overflow-y-auto overscroll-contain rounded-[16px] border border-[#EAE8F5] bg-white p-1.5 shadow-[0_12px_32px_-12px_rgba(60,40,120,0.18)]"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-[#7C8B88]">
              No account types available
            </li>
          ) : (
            options.map((item) => {
              const active = item.key === value;
              return (
                <li key={item.key} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={`flex w-full items-center rounded-[10px] border-none px-3 py-2.5 text-left text-sm transition-colors ${
                      active
                        ? 'bg-[#E8E5F4] font-semibold text-[#3A3660]'
                        : 'bg-transparent text-[#3A3660] hover:bg-[#F3F2F9]'
                    }`}
                    onClick={() => {
                      onChange(item.key);
                      setOpen(false);
                      onBlur?.();
                    }}
                  >
                    <span className="truncate">{item.label}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
